/**
 * Unassigned Tasks Panel Component
 * Panel showing tasks that are not assigned to any route
 */

import { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Badge, Input } from '../ui';
import { Package, RotateCcw, MapPin, Search, GripVertical, Banknote } from 'lucide-react';
import { cn } from '../../utils/cn';
import { matchesSearch } from '../../utils/search';
import type { RoutingTask } from '../../types';

interface UnassignedTasksPanelProps {
  tasks: RoutingTask[];
  onAssignTask: (taskUid: string, routeId: string) => Promise<void>;
  availableRouteIds: { id: string; driverName: string }[];
  isLoading?: boolean;
}

export default function UnassignedTasksPanel({
  tasks,
  isLoading,
}: UnassignedTasksPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedTaskUid, setDraggedTaskUid] = useState<string | null>(null);

  const filteredTasks = tasks.filter((task) =>
    // Normalised both sides: Core keeps doubled spaces in merchant names that
    // HTML collapses on screen, so a raw includes() hides the row the officer
    // is looking at. See utils/search.ts.
    matchesSearch(searchQuery, task.uid, task.merchant_name, task.area_name)
  );

  const handleDragStart = useCallback((e: React.DragEvent, taskUid: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskUid);
    e.dataTransfer.setData('application/x-task-uid', taskUid);
    setDraggedTaskUid(taskUid);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedTaskUid(null);
  }, []);

  const getTaskIcon = (type: RoutingTask['type']) => {
    return type === 'pickup' ? (
      <Package className="h-4 w-4 text-green-600" />
    ) : type === 'cash' ? (
      <Banknote className="h-4 w-4 text-amber-600" />
    ) : (
      <RotateCcw className="h-4 w-4 text-yellow-600" />
    );
  };

  if (isLoading) {
    return (
      <Card padding="none">
        <CardHeader className="px-4 pt-4">
          <CardTitle className="text-base">Unassigned Tasks</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card padding="none" className="flex flex-col h-full">
      <CardHeader className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Unassigned Tasks
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({tasks.length})
            </span>
          </CardTitle>
        </div>
      </CardHeader>

      {/* Search */}
      <div className="px-4 pb-3">
        <Input
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
          size="sm"
        />
      </div>

      {/* Tasks List */}
      <CardContent className="px-4 pb-4 flex-1 overflow-y-auto">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">
              {tasks.length === 0
                ? 'All tasks are assigned'
                : 'No matching tasks'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map((task) => {
              const isDragging = draggedTaskUid === task.uid;

              return (
                <div
                  key={task.uid}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task.uid)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-white',
                    'hover:border-gray-300 hover:shadow-sm cursor-grab active:cursor-grabbing',
                    'transition-all',
                    isDragging && 'opacity-50 scale-95'
                  )}
                >
                  {/* Drag Handle */}
                  <div className="text-gray-400">
                    <GripVertical className="h-4 w-4" />
                  </div>

                  {/* Task Icon */}
                  {getTaskIcon(task.type)}

                  {/* Task Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {task.uid}
                      </span>
                      <Badge
                        variant={task.type === 'pickup' ? 'success' : task.type === 'cash' ? 'warning' : 'warning'}
                        size="sm"
                      >
                        {task.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">
                        {task.merchant_name || task.area_name || `${task.lat.toFixed(4)}, ${task.lng.toFixed(4)}`}
                      </span>
                    </div>
                  </div>

                  {/* Parcel Count */}
                  <div className="text-xs text-gray-500">
                    {task.number_of_orders} pkg
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Help Text */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 text-center">
          Drag tasks to a route to assign them
        </p>
      </div>
    </Card>
  );
}
